/**
 * TeamMemberOnboarding — 5-step Linear-style welcome flow for newly
 * invited team members (role !== "super_admin"). The existing
 * OnboardingWizard is a super-admin-only dialog for FRESH TENANT setup;
 * that's a different mental model (workspace creator) so we intentionally
 * do NOT reuse it here.
 *
 * Design principles (Linear-tarzı):
 *   - Full-page, minimal chrome, no sidebar/topbar noise
 *   - Serpix violet accent, sharp typography, generous whitespace
 *   - Progress dots at top, "Atla" micro-link top-right (never trap)
 *   - Each step ends with a single obvious next action
 *   - Step 3 creates a REAL task (aha moment), not a mock
 *   - Step 4 fires the real Web Push permission prompt inline
 *
 * On finish/skip → POST /users/me/complete-onboarding → /app/dashboard.
 * Backend already flips `onboarding_completed=true`; AppShell then stops
 * routing here.
 */
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  MessageSquare,
  ListChecks,
  FolderOpen,
  Bell,
  Rocket,
  Sparkles,
  Home,
  Users,
  Settings,
  BellRing,
  Loader2,
} from "lucide-react";

import { api, formatApiErrorDetail } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { subscribe as subscribePush, isSupported as isPushSupported } from "../lib/push";

const TOTAL_STEPS = 5;

export default function TeamMemberOnboarding() {
  const navigate = useNavigate();
  const { user, tenant, fetchMe } = useAuthStore();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const firstName = useMemo(
    () => (user?.name || user?.email || "").split(" ")[0] || "orada",
    [user?.name, user?.email],
  );

  const complete = useCallback(async () => {
    try {
      await api.post("/users/me/complete-onboarding");
      await fetchMe?.();
    } catch (err) {
      // If this ever 500s and we just push to /dashboard silently, AppShell
      // would bounce the user right back here → infinite loop. Surface the
      // error so ops sees it AND keep the user on the onboarding page so
      // they can retry with the CTA. Non-fatal auth issues just fall
      // through and we let AppShell decide.
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Onboarding tamamlanamadı, birazdan tekrar dene.",
      );
      return;
    }
    navigate("/app/dashboard", { replace: true });
  }, [fetchMe, navigate]);

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const stepProps = { firstName, user, tenant, busy, setBusy, next, complete };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-zinc-50 via-white to-violet-50/40"
      data-testid="team-onboarding-root"
    >
      {/* Header — logo + skip */}
      <header className="mx-auto flex max-w-3xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-violet-600 text-[13px] font-bold text-white">
            S
          </span>
          Serpix
        </div>
        <button
          type="button"
          onClick={complete}
          className="text-xs text-zinc-400 transition hover:text-zinc-700"
          data-testid="onboarding-skip"
        >
          Atla ve dashboard&apos;a git
        </button>
      </header>

      {/* Progress dots */}
      <div className="mx-auto flex max-w-3xl items-center justify-center gap-1.5 px-6 pb-4">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 w-8 rounded-full transition-all ${
              i === step
                ? "bg-violet-600"
                : i < step
                  ? "bg-violet-300"
                  : "bg-zinc-200"
            }`}
            data-testid={`onboarding-progress-${i}`}
          />
        ))}
      </div>

      {/* Body */}
      <main className="mx-auto max-w-3xl px-6 pb-16">
        <div className="rounded-2xl border border-zinc-200 bg-white/90 p-8 shadow-sm backdrop-blur md:p-12">
          {step === 0 && <StepWelcome {...stepProps} />}
          {step === 1 && <StepOverview {...stepProps} />}
          {step === 2 && <StepFirstTask {...stepProps} />}
          {step === 3 && <StepNotifications {...stepProps} />}
          {step === 4 && <StepTour {...stepProps} />}
        </div>

        {/* Nav */}
        <div className="mt-6 flex items-center justify-between px-2">
          <button
            type="button"
            onClick={back}
            disabled={step === 0 || busy}
            className="inline-flex items-center gap-1.5 text-xs text-zinc-500 transition hover:text-zinc-800 disabled:cursor-not-allowed disabled:opacity-30"
            data-testid="onboarding-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Geri
          </button>
          <div className="text-xs text-zinc-400">
            {step + 1} / {TOTAL_STEPS}
          </div>
        </div>
      </main>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * STEP 1 — Welcome
 * ──────────────────────────────────────────────────────────────────── */
function StepWelcome({ firstName, tenant, next }) {
  return (
    <div className="space-y-6" data-testid="onboarding-step-welcome">
      <div className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-[11px] font-medium text-violet-700">
        <Sparkles className="h-3 w-3" />
        Hoş geldin
      </div>
      <h1 className="text-3xl font-bold text-zinc-900 md:text-4xl">
        Selam {firstName} 👋
      </h1>
      <p className="text-base leading-relaxed text-zinc-600 md:text-lg">
        <span className="font-medium text-zinc-800">
          {tenant?.name || "Ekibin"}
        </span>{" "}
        seni Serpix&apos;e davet etti. Serpix; ekip görevlerinizi,
        mesajlarınızı ve dosyalarınızı tek yerde toplayan bir çalışma alanı.
      </p>
      <p className="text-sm text-zinc-500">
        Bir sonraki 3 dakikada 4 şeyi göstereceğim. İstediğin zaman
        atlayabilir, sonra ayarlardan geri dönebilirsin.
      </p>
      <div className="pt-4">
        <Button
          onClick={next}
          size="lg"
          className="bg-violet-600 hover:bg-violet-700"
          data-testid="onboarding-welcome-continue"
        >
          Başlayalım <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * STEP 2 — Overview
 * ──────────────────────────────────────────────────────────────────── */
function StepOverview({ next }) {
  const items = [
    {
      Icon: ListChecks,
      title: "Görev yönet",
      desc: "Kanban ile takip et, kime atandığını gör, teslim tarihini kaçırma.",
    },
    {
      Icon: MessageSquare,
      title: "Anlık iletişim",
      desc: "DM, kanal, yorum — mail'e boğulmadan konuş.",
    },
    {
      Icon: FolderOpen,
      title: "Dosya paylaş",
      desc: "Sözleşmeler, teklifler, fotoğraflar hepsi tek yerde.",
    },
  ];
  return (
    <div className="space-y-8" data-testid="onboarding-step-overview">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900 md:text-3xl">
          Serpix ile neler yapabilirsin?
        </h2>
        <p className="mt-2 text-sm text-zinc-500">
          Üç ana yetenek — hepsi tek uygulamada.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {items.map(({ Icon, title, desc }) => (
          <div
            key={title}
            className="rounded-xl border border-zinc-200 bg-white p-5 transition hover:border-violet-200 hover:shadow-sm"
          >
            <Icon className="h-6 w-6 text-violet-600" />
            <div className="mt-3 text-sm font-semibold text-zinc-900">
              {title}
            </div>
            <div className="mt-1 text-xs leading-relaxed text-zinc-500">
              {desc}
            </div>
          </div>
        ))}
      </div>
      <div className="pt-2">
        <Button
          onClick={next}
          size="lg"
          className="bg-violet-600 hover:bg-violet-700"
          data-testid="onboarding-overview-continue"
        >
          Anladım <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * STEP 3 — Create first task (real DB write)
 * ──────────────────────────────────────────────────────────────────── */
function StepFirstTask({ user, busy, setBusy, next }) {
  const [title, setTitle] = useState("Serpix'i keşfet");
  const [dueDays, setDueDays] = useState("2");
  const [noProjectYet, setNoProjectYet] = useState(false);

  const submit = async () => {
    if (!title.trim()) {
      toast.error("Görev başlığı boş olamaz.");
      return;
    }
    setBusy(true);
    try {
      // Find an existing project + list the user can post into. Regular
      // users (role=user) typically don't have `can_create_projects`, so
      // we DO NOT try to create a project here — that would 403 and look
      // like a bug. If nothing exists yet we surface a friendly message
      // and let them skip.
      //
      // Contract note (iter57 regression from testing agent): /api/projects
      // returns projects WITHOUT an embedded `lists` field — lists live at
      // /api/lists?project_id=<pid>. We loop projects one-by-one until we
      // find one with at least one list. Per-project try/catch so a single
      // 403/404 doesn't kill the discovery.
      let projectId = null;
      let listId = null;
      try {
        const { data: projects } = await api.get("/projects");
        for (const p of projects || []) {
          try {
            const { data: lists } = await api.get("/lists", {
              params: { project_id: p.id },
            });
            if (Array.isArray(lists) && lists.length > 0) {
              projectId = p.id;
              listId = lists[0].id;
              break;
            }
          } catch {
            /* this project not accessible for this user — try next */
          }
        }
      } catch {
        /* projects endpoint failed — treat as empty */
      }

      if (!projectId || !listId) {
        setNoProjectYet(true);
        return;
      }

      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + Number.parseInt(dueDays || "2", 10));

      await api.post("/tasks", {
        project_id: projectId,
        list_id: listId,
        title: title.trim(),
        description:
          "Ekibinle merhaba de, ilk mesajını at, ayarlar sayfasına göz at.",
        assignee_id: user.id,
        due_date: dueDate.toISOString(),
        priority: "medium",
      });
      toast.success("İlk görevin oluşturuldu 🎉");
      next();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Görev oluşturulamadı, atlamak istersen 'Atla'yı kullanabilirsin.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (noProjectYet) {
    return (
      <div className="space-y-6" data-testid="onboarding-step-first-task">
        <div>
          <h2 className="text-2xl font-bold text-zinc-900 md:text-3xl">
            Henüz bir proje yok
          </h2>
          <p className="mt-2 text-sm text-zinc-500">
            Ekip yöneticin bir proje oluşturunca ilk görevini
            hazırlayabilirsin. Şimdilik bu adımı atlayıp bildirim ayarlarına
            geçelim — yöneticine haber verebilirsin.
          </p>
        </div>
        <Button
          onClick={next}
          size="lg"
          className="bg-violet-600 hover:bg-violet-700"
          data-testid="onboarding-task-continue-noproject"
        >
          Devam <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="onboarding-step-first-task">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900 md:text-3xl">
          İlk görevini oluştur
        </h2>
        <p className="mt-2 text-sm text-zinc-500">
          Yaparak öğrenmek en hızlısı. Kendine küçük bir görev ata —
          dashboard&apos;da hemen görürsün.
        </p>
      </div>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="task-title" className="text-xs text-zinc-600">
            Başlık
          </Label>
          <Input
            id="task-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
            data-testid="onboarding-task-title-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="task-due" className="text-xs text-zinc-600">
            Teslim
          </Label>
          <select
            id="task-due"
            value={dueDays}
            onChange={(e) => setDueDays(e.target.value)}
            disabled={busy}
            className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
            data-testid="onboarding-task-due-select"
          >
            <option value="0">Bugün</option>
            <option value="1">Yarın</option>
            <option value="2">2 gün sonra</option>
            <option value="7">1 hafta sonra</option>
          </select>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-2">
        <Button
          onClick={submit}
          disabled={busy}
          size="lg"
          className="bg-violet-600 hover:bg-violet-700"
          data-testid="onboarding-task-create"
        >
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Oluşturuluyor…
            </>
          ) : (
            <>
              Görev oluştur <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
        <Button
          onClick={next}
          disabled={busy}
          size="lg"
          variant="ghost"
          className="text-zinc-500"
          data-testid="onboarding-task-skip"
        >
          Şimdilik atla
        </Button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * STEP 4 — Notifications (real Web Push permission prompt)
 * ──────────────────────────────────────────────────────────────────── */
function StepNotifications({ busy, setBusy, next }) {
  const [status, setStatus] = useState("idle"); // idle | granted | denied | unsupported
  const supported = isPushSupported();

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      setStatus("granted");
      toast.success("Bildirimler aktif ✔");
    } catch (err) {
      const msg = String(err?.message || "").toLowerCase();
      if (msg.includes("denied") || msg.includes("permission")) {
        setStatus("denied");
        toast.error(
          "İzin reddedildi. Tarayıcı ayarlarından açabilirsin — istersen sonra Ayarlar > Bildirimler'den de tetikleyebilirsin.",
        );
      } else {
        toast.error(
          "Bildirim aboneliği kurulamadı, sonra Ayarlar > Bildirimler sayfasından tekrar deneyebilirsin.",
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-step-notifications">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900 md:text-3xl">
          Bildirimleri aç 🔔
        </h2>
        <p className="mt-2 text-sm text-zinc-500">
          Görev atandığında, sana DM geldiğinde ya da yorumda @&apos;lendiğinde
          hemen haberin olsun. Telefonun kilit ekranında bile.
        </p>
      </div>

      {!supported && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
          Bu tarayıcı push bildirimlerini desteklemiyor. iPhone kullanıyorsan
          Safari&apos;de <b>Paylaş → Ana Ekrana Ekle</b> yaparak Serpix&apos;i
          uygulama gibi ekle, sonra o ikondan girip bu adımı tekrar dene.
        </div>
      )}

      {supported && status !== "granted" && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-5">
          <div className="flex items-start gap-3">
            <BellRing className="mt-0.5 h-5 w-5 flex-shrink-0 text-violet-600" />
            <div className="flex-1 text-xs text-violet-900">
              <p className="font-medium">
                &quot;İzin Ver&quot;e tıkladığında tarayıcı sana bir izin
                soracak.
              </p>
              <p className="mt-1 text-violet-700">
                Kabul edersen 1 saniye içinde ilk test bildirimini alacaksın.
              </p>
            </div>
          </div>
        </div>
      )}

      {status === "granted" && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          Bildirimler açıldı. Test bildirimi kısa süre içinde ekranında
          görünecek.
        </div>
      )}

      {status === "denied" && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-xs text-rose-900">
          İzin reddedildi. Sonra Ayarlar &gt; Bildirim Tercihleri sayfasından
          tekrar deneyebilirsin (tarayıcı adres çubuğundaki kilit ikonundan da
          değiştirebilirsin).
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2">
        {status !== "granted" && supported && (
          <Button
            onClick={enable}
            disabled={busy}
            size="lg"
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="onboarding-notifications-enable"
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                İzin isteniyor…
              </>
            ) : (
              <>
                <Bell className="mr-2 h-4 w-4" />
                İzin Ver
              </>
            )}
          </Button>
        )}
        <Button
          onClick={next}
          disabled={busy}
          size="lg"
          variant={status === "granted" ? "default" : "ghost"}
          className={
            status === "granted" ? "bg-violet-600 hover:bg-violet-700" : "text-zinc-500"
          }
          data-testid="onboarding-notifications-continue"
        >
          {status === "granted" ? (
            <>
              Devam <ArrowRight className="ml-2 h-4 w-4" />
            </>
          ) : (
            "Sonra bakarım"
          )}
        </Button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * STEP 5 — Tour + Finish
 * ──────────────────────────────────────────────────────────────────── */
function StepTour({ complete, busy }) {
  const items = [
    { Icon: Home, label: "Kontrol Merkezi", desc: "Günün özeti + sana atanan görevler" },
    { Icon: ListChecks, label: "Görevlerim", desc: "Sana atanan işler, kanban görünümü" },
    { Icon: MessageSquare, label: "Sohbet", desc: "DM'ler + kanal mesajları" },
    { Icon: Users, label: "Kişiler", desc: "Ekibin + müşteri kontakları" },
    { Icon: Settings, label: "Ayarlar", desc: "Bildirim tercihleri + profil" },
  ];
  return (
    <div className="space-y-8" data-testid="onboarding-step-tour">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-medium text-emerald-700">
          <Rocket className="h-3 w-3" />
          Hazırsın
        </div>
        <h2 className="mt-3 text-2xl font-bold text-zinc-900 md:text-3xl">
          Sol menüde neler var?
        </h2>
        <p className="mt-2 text-sm text-zinc-500">
          Kısaca — geri kalanını kullandıkça keşfedeceksin.
        </p>
      </div>
      <ul className="space-y-2">
        {items.map(({ Icon, label, desc }) => (
          <li
            key={label}
            className="flex items-start gap-3 rounded-lg border border-zinc-100 bg-zinc-50/50 p-3"
          >
            <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-violet-600" />
            <div>
              <div className="text-sm font-medium text-zinc-900">{label}</div>
              <div className="text-xs text-zinc-500">{desc}</div>
            </div>
          </li>
        ))}
      </ul>
      <div className="rounded-lg border border-zinc-200 bg-white p-3 text-[11px] text-zinc-500">
        💡 İhtiyacın olursa sağ üstteki profil menüsünden bu turu her zaman
        tekrar başlatabilirsin.
      </div>
      <div className="pt-2">
        <Button
          onClick={complete}
          disabled={busy}
          size="lg"
          className="bg-violet-600 hover:bg-violet-700"
          data-testid="onboarding-finish"
        >
          Dashboard&apos;a git <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
